/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useLookupSourceList.ts
 🗓️ Created: 2025-01-28
 🧩 Description:
    Custom hook for managing Caller ID Lookup Sources list.
    Integrates with useDataGrid for pagination, sorting, and search.
    Supports Domain Contextual Filtering pattern.

 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added Domain Filtering)
────────────────────────────────────────────────────────────────*/

import { useCallback, useEffect } from 'react';
import { useDataGrid, DataGridColumn } from '../../../hooks/useDataGrid';
import { useContextStore } from '../../../store/context';
import { fetchLookupSources } from '../api';
import { CallerIdLookupSource } from '../types';

interface UseLookupSourceListOptions {
  domainId?: number;
  onRefreshNeeded?: () => void;
  initialColumns?: DataGridColumn<CallerIdLookupSource>[];
}

/**
 * Custom hook for Lookup Sources list
 * Implements Domain Contextual Filtering pattern
 */
export function useLookupSourceList(options: UseLookupSourceListOptions = {}) {
  const { selectedDomain } = useContextStore();

  // Use provided domainId or fallback to selected domain
  const effectiveDomainId = options.domainId ?? selectedDomain?.id;

  const fetchData = useCallback(
    async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      try {
        const result = await fetchLookupSources({
          ...params,
          domainId: effectiveDomainId,
        });
        return {
          data: result.data || [],
          pagination: result.pagination || {
            page: 1,
            limit: 10,
            total: 0,
            totalPages: 1,
          },
        };
      } catch (error) {
        return {
          data: [] as CallerIdLookupSource[],
          pagination: { page: 1, limit: 10, total: 0, totalPages: 0 },
        };
      }
    },
    [effectiveDomainId],
  );

  const gridResult = useDataGrid({
    fetchData,
    initialColumns: options.initialColumns,
    initialSortBy: 'name',
    initialSortOrder: 'asc',
    initialLimit: 10,
    moduleName: 'callerIdLookupSources',
    reloadKey: effectiveDomainId ?? null,
  });

  // Trigger refresh when domainId changes
  useEffect(() => {
    if (effectiveDomainId !== undefined && options.onRefreshNeeded) {
      options.onRefreshNeeded();
    }
  }, [effectiveDomainId]);

  return {
    ...gridResult,
    effectiveDomainId,
  };
}

